import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { Heart, MessageCircle, Share2, Music2, Volume2, VolumeX, Trash2, Play, Reply } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global mute preference - persisted in localStorage
const getMutePreference = () => {
  const saved = localStorage.getItem('videoMuted');
  // Default to unmuted (sound ON) if no preference saved
  return saved === 'true';
};

const setMutePreference = (muted) => {
  localStorage.setItem('videoMuted', muted.toString());
};

const FeedItem = ({ post, onCommentClick, onLikeUpdate, onDelete }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const videoRef = useRef(null);
  // Initialize mute state from localStorage (default: unmuted/sound ON)
  const [muted, setMuted] = useState(getMutePreference);
  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [showHeart, setShowHeart] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  // Handle video autoplay based on visibility
  useEffect(() => {
    if (post.media_type === 'video' && videoRef.current) {
      const video = videoRef.current;
      
      if (inView) {
        // Always apply user's mute preference to video element
        video.muted = muted;
        
        // Load the video first (important for iOS with multiple sources)
        video.load();
        
        // Try to play with user's preference
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch((err) => {
              // Autoplay blocked (likely because sound is ON)
              // Show play button - user will tap to play with sound
              console.log('Autoplay blocked:', err.name);
              setIsPlaying(false);
            });
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  }, [inView, post.media_type, muted]);

  // Sync mute state changes to video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  const handleToggleMute = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    setMutePreference(newMuted); // Persist preference
    if (videoRef.current) {
      videoRef.current.muted = newMuted;
    }
  };

  const handleLike = async () => {
    try {
      if (liked) {
        await axios.delete(`${API}/posts/${post.id}/like`);
        setLiked(false);
        setLikesCount(prev => prev - 1);
      } else {
        await axios.post(`${API}/posts/${post.id}/like`);
        setLiked(true);
        setLikesCount(prev => prev + 1);
        setShowHeart(true);
        setTimeout(() => setShowHeart(false), 800);
      }
      onLikeUpdate?.(post.id, !liked);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update like');
    }
  };

  const handleDoubleTap = () => {
    if (!liked) {
      handleLike();
    } else {
      setShowHeart(true);
      setTimeout(() => setShowHeart(false), 800);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/?id=${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied!');
      }
    } catch {
      // User cancelled share
    }
  };

  const handleProfileClick = () => {
    navigate(`/profile/${post.performer_id}`);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/posts/${post.id}`);
      toast.success('Post deleted');
      setShowDeleteDialog(false);
      onDelete?.(post.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  // Helper to get video sources for iOS compatibility
  // Returns an object with HLS and MP4 URLs for Cloudinary videos
  const getVideoSources = (url) => {
    if (!url) return { mp4: '', hls: '' };
    
    // For Cloudinary URLs, generate both HLS and MP4 versions
    if (url.includes('res.cloudinary.com')) {
      // Extract the base URL parts
      // Format: https://res.cloudinary.com/cloud_name/video/upload/[transforms]/v123/path.mp4
      
      // Generate HLS URL (change extension to .m3u8 and add streaming profile)
      let hlsUrl = url;
      if (url.includes('/upload/v')) {
        hlsUrl = url.replace('/upload/v', '/upload/sp_auto/v');
      } else if (url.includes('/upload/')) {
        hlsUrl = url.replace('/upload/', '/upload/sp_auto/');
      }
      // Change extension to m3u8
      hlsUrl = hlsUrl.replace(/\.(mp4|mov|avi|webm)$/i, '.m3u8');
      
      // Generate MP4 URL with H.264 codec
      let mp4Url = url;
      if (url.includes('/upload/v')) {
        mp4Url = url.replace('/upload/v', '/upload/vc_h264,f_mp4/v');
      } else if (url.includes('/upload/')) {
        mp4Url = url.replace('/upload/', '/upload/vc_h264,f_mp4/');
      }
      
      return { hls: hlsUrl, mp4: mp4Url };
    }
    
    // For non-Cloudinary URLs
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return { mp4: url, hls: '' };
    }
    
    // Local files
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return { mp4: fullUrl, hls: '' };
  };

  // Simple helper for images and backward compatibility
  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Handle tap to play video (for iOS fallback)
  const handleVideoTap = () => {
    if (post.media_type === 'video' && videoRef.current) {
      const video = videoRef.current;
      
      if (video.paused) {
        // Apply user's mute preference (default: sound ON)
        video.muted = muted;
        video.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {
          // Play was blocked even with user interaction - shouldn't normally happen
          setIsPlaying(false);
        });
      } else {
        handleToggleMute();
      }
    }
  };

  const username = post.performer_email?.split('@')[0] || 'user';
  
  // Get video sources for the current post
  const videoSources = post.media_type === 'video' ? getVideoSources(post.media_url) : null;

  return (
    <div
      ref={ref}
      className="snap-item relative w-full bg-black flex items-center justify-center"
      data-testid={`feed-item-${post.id}`}
    >
      {/* Media Container */}
      <div 
        className="absolute inset-0 flex items-center justify-center"
        onDoubleClick={handleDoubleTap}
      >
        {post.media_type === 'video' ? (
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-contain"
              loop
              muted={muted}
              playsInline
              preload="auto"
              poster={post.poster}
              onClick={handleVideoTap}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={(e) => {
                console.log('Video error:', e.target.error);
              }}
              data-testid={`video-${post.id}`}
            >
              {/* HLS source for iOS - Safari supports HLS natively */}
              {videoSources?.hls && (
                <source src={videoSources.hls} type="application/x-mpegURL" />
              )}
              {/* MP4 fallback for other browsers */}
              <source src={videoSources?.mp4} type="video/mp4" />
            </video>
            {/* Play button overlay when video isn't playing */}
            {!isPlaying && inView && (
              <button
                onClick={handleVideoTap}
                className="absolute inset-0 flex items-center justify-center bg-black/20 z-10"
                data-testid={`play-overlay-${post.id}`}
              >
                <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" fill="white" />
                </div>
              </button>
            )}
          </>
        ) : (
          <img
            src={getMediaUrl(post.media_url)}
            alt={post.caption || 'Post'}
            className="w-full h-full object-contain"
            data-testid={`image-${post.id}`}
          />
        )}
      </div>

      {/* Double tap heart animation */}
      <AnimatePresence>
        {showHeart && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-40"
          >
            <Heart className="w-24 h-24 text-zom-red fill-zom-red" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 gradient-overlay pt-32 pb-20 px-4 z-20">
        {/* Username - clickable to go to profile */}
        <button 
          onClick={handleProfileClick}
          className="flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity"
          data-testid="post-username-link"
        >
          <span className="font-heading font-bold text-white text-shadow">
            @{username}
          </span>
        </button>

        {/* Caption - with clickable links */}
        {post.caption && (
          <p className="text-white text-sm text-shadow line-clamp-3 mb-2 max-w-[280px] font-body" data-testid="post-caption">
            {post.caption.split(/(https?:\/\/[^\s]+)/g).map((part, i) => 
              part.match(/^https?:\/\//) ? (
                <a 
                  key={i}
                  href={part}
                  onClick={(e) => {
                    e.preventDefault();
                    const match = part.match(/[?&]id=([^&]+)/);
                    if (match && part.includes('zombify.life')) {
                      navigate(`/?id=${match[1]}`);
                    } else {
                      window.open(part, '_blank');
                    }
                  }}
                  className="text-zom-yellow underline hover:text-zom-red transition-colors"
                >
                  {part.includes('zombify.life') ? 'original post' : part}
                </a>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </p>
        )}

        {/* Top Responses */}
        {post.top_responses && post.top_responses.length > 0 && (
          <div className="mt-2 mb-2">
            <p className="text-white/60 text-xs mb-1 font-heading">TOP RESPONSES:</p>
            <div className="flex gap-2">
              {post.top_responses.map((response) => (
                <button
                  key={response.id}
                  onClick={() => navigate(`/?id=${response.id}`)}
                  className="flex items-center gap-1 bg-zom-black/60 border border-zom-yellow px-2 py-1 hover:bg-zom-yellow hover:text-zom-black transition-colors"
                  data-testid={`response-link-${response.id}`}
                >
                  <span className="text-white text-xs font-heading">@{response.performer_email?.split('@')[0]?.toUpperCase()}</span>
                  <Heart className="w-3 h-3 text-zom-red fill-zom-red" strokeWidth={2.5} />
                  <span className="text-white/70 text-xs font-heading">{response.likes_count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sound/Assignment */}
        <div className="flex items-center gap-2 text-white/70 text-xs">
          <Music2 className="w-3 h-3" />
          <span className="truncate max-w-[200px]">{post.assignment_title}</span>
        </div>
      </div>

      {/* Right Action Bar */}
      <div className="absolute right-3 bottom-32 flex flex-col items-center gap-5 z-30">
        {/* Avatar - clickable */}
        <button 
          onClick={handleProfileClick}
          className="w-11 h-11 bg-zom-yellow border-3 border-zom-black overflow-hidden mb-2 hover:scale-105 transition-transform"
          data-testid="post-avatar-link"
        >
          <div className="w-full h-full bg-zom-yellow flex items-center justify-center">
            <span className="text-zom-black font-heading font-bold text-sm">
              {username[0]?.toUpperCase()}
            </span>
          </div>
        </button>

        {/* Like Button */}
        <button
          onClick={handleLike}
          className="action-btn flex flex-col items-center"
          data-testid={`like-btn-${post.id}`}
        >
          <Heart
            className={`w-8 h-8 ${liked ? 'text-zom-red fill-zom-red' : 'text-white'} transition-colors`}
            strokeWidth={2.5}
          />
          <span className="text-white text-xs mt-1 font-heading">{likesCount}</span>
        </button>

        {/* Comment Button */}
        <button
          onClick={() => onCommentClick?.(post)}
          className="action-btn flex flex-col items-center"
          data-testid={`comment-btn-${post.id}`}
        >
          <MessageCircle className="w-8 h-8 text-white" strokeWidth={2.5} />
          <span className="text-white text-xs mt-1 font-heading">{post.comments_count}</span>
        </button>

        {/* Respond Button - visible to all, navigates performers to upload */}
        <button
          onClick={() => {
            if (isAdmin) {
              toast.info('Only performers can respond to posts');
            } else {
              navigate(`/upload?respondTo=${post.id}`);
            }
          }}
          className="action-btn flex flex-col items-center"
          data-testid={`respond-btn-${post.id}`}
        >
          <Reply className="w-7 h-7 text-white" strokeWidth={2.5} />
          <span className="text-white text-xs mt-1 font-heading">{post.responses_count || 0}</span>
        </button>

        {/* Share Button */}
        <button
          onClick={handleShare}
          className="action-btn flex flex-col items-center"
          data-testid={`share-btn-${post.id}`}
        >
          <Share2 className="w-7 h-7 text-white" strokeWidth={2.5} />
        </button>

        {/* Mute Toggle (for videos) */}
        {post.media_type === 'video' && (
          <button
            onClick={handleToggleMute}
            className="action-btn"
            data-testid={`mute-btn-${post.id}`}
          >
            {muted ? (
              <VolumeX className="w-6 h-6 text-white/70" strokeWidth={2.5} />
            ) : (
              <Volume2 className="w-6 h-6 text-white" strokeWidth={2.5} />
            )}
          </button>
        )}

        {/* Delete Button (Admin only) */}
        {isAdmin && (
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="action-btn flex flex-col items-center mt-2"
            data-testid={`delete-btn-${post.id}`}
          >
            <Trash2 className="w-6 h-6 text-zom-red" strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-zom-black border-3 border-zom-yellow text-zom-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zom-yellow">DELETE POST?</AlertDialogTitle>
            <AlertDialogDescription className="text-zom-white/70 font-body">
              This will permanently delete this post from the feed, including all likes and comments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zom-black border-2 border-zom-white text-zom-white hover:bg-zom-white hover:text-zom-black font-heading">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-zom-red hover:bg-zom-red/80 text-zom-white font-heading border-2 border-zom-black"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'DELETING...' : 'DELETE'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeedItem;
